import { remoteProps } from "#ce/components/SpQuantumSelect/props";
import { booleanAttribute } from "#ce/utils/props";

// `value` is absent on purpose: the element owns a live `value` property, which a declared prop
// would shadow with an accessor onto the prop store. The attribute seeds it and names the reset value.
export const props = Object.freeze({
  ...remoteProps,
  /**
   * Array of items the dropdown filters against. Each entry is either a plain string (used for both the title and the value) or an object whose `title`/`value` keys are read by default. Pass via `.prop` because arrays cannot be expressed as attribute strings.
   *
   * @type {Array<String|Object>}
   * @default []
   */
  items: {
    type: Array,
    default: () => [],
  },
  /**
   * Key on each item that holds the visible label used for filtering and rendering.
   *
   * @type {String}
   * @default "title"
   */
  itemTitle: {
    type: String,
    default: "title",
  },
  /**
   * Property name on item objects that holds the value. Names the key the submitted form value is
   * read from under `return-object`, and the key the inner select matches a selection on.
   *
   * @type {String}
   * @default "value"
   */
  itemValue: {
    type: String,
    default: "value",
  },
  /**
   * Hand the whole item object back as the selection instead of its value.
   *
   * @type {Boolean|String}
   * @default false
   */
  returnObject: booleanAttribute(),
  /**
   * Allow more than one selection. The live `value` is then an array, and a form receives one entry
   * per selection under `name`.
   *
   * @type {Boolean|String}
   * @default false
   */
  multiple: booleanAttribute(),
  /**
   * Drop the typed search after each selection, so a server-driven list empties until the user
   * types again. Meaningful with `keep-open-on-select`; a closing picker drops the search anyway.
   *
   * @type {Boolean|String}
   * @default false
   */
  clearOnSelect: booleanAttribute(),
  /**
   * Current text inside the input. Bind this and listen for `update-search` to drive external (server-side) filtering. Leave unset to let the component manage its own search state.
   *
   * @type {String}
   * @default undefined
   */
  search: {
    type: String,
    default: undefined,
  },
  /**
   * Disable the built-in client-side filtering. Set this when the items array is already filtered by a server-side query keyed off `update-search`.
   *
   * @type {Boolean|String}
   * @default false
   */
  noFilter: booleanAttribute(),
  /**
   * Hide the "no data" row entirely. Without it the row shows while a search matches nothing. The
   * picker is held closed while there is nothing to list and no search either way, since the row is
   * hidden until the user types.
   *
   * @type {Boolean|String}
   * @default false
   */
  hideNoData: booleanAttribute(),
  /**
   * Sets a `data-testid` attribute on every rendered item row. Pass a catalog value from the
   * feature-specific caller (see `app/frontend/src/test-ids.json`).
   *
   * @type {String}
   * @default undefined
   */
  itemTestId: {
    type: String,
    default: undefined,
  },
  /**
   * Highlight the first matching row while the user types; Enter or Tab selects it.
   *
   * @type {Boolean|String}
   * @default false
   */
  autoSelectFirst: booleanAttribute(),
  /**
   * Empty-state message shown inside the dropdown once the user has typed but no item matches.
   *
   * @type {String}
   * @default "No results found"
   */
  noDataText: {
    type: String,
    default: "No results found",
  },
  /**
   * Whether the control is disabled.
   *
   * @type {Boolean|String}
   * @default false
   */
  disabled: booleanAttribute(),
  /**
   * Require a selection before the form can be submitted. Presence-based, the way the platform
   * reads a boolean attribute: any value other than `false` constrains the field.
   *
   * @type {Boolean|String}
   * @default false
   */
  required: booleanAttribute(),
  /**
   * Message the browser shows when a required field is submitted without a selection. Names what
   * this particular field is for; the generic wording comes from `frontend.forms`.
   *
   * @type {String}
   * @default undefined
   */
  requiredMessage: {
    type: String,
    default: undefined,
  },
});
